import Preloader from "@common/controls/Preloader/component";
import { SystemColor } from "@common/declarations";
import { default as classNames, default as classnames } from "classnames";
import * as React from "react";

import { getTooltipAttributes } from "@common/contexts/Tooltip";

import "./style.less";

export type IconProps = React.HTMLProps<HTMLSpanElement> & {
  icon: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  size?: number;
  title?: string;
  color?: SystemColor | string;
  hoverColor?: SystemColor | string;
  tooltip?: string | null;
};

const Icon = ({
  icon,
  title,
  color,
  className,
  hoverColor,
  isDisabled,
  onClick,
  size,
  tooltip,
  ...props
}: IconProps) => {
  const style = React.useMemo(() => {
    const style = {
      ...props.style,
      height: size ?? props.style?.height,
      width: size ?? props.style?.width
    };

    if (hoverColor) {
      style["--hover-color"] = hoverColor;
    }

    return style;
  }, [size, props.style]);

  const finalColor = React.useMemo(() => {
    switch (color) {
      case SystemColor.Blue:
      case SystemColor.Gray:
      case SystemColor.Green:
      case SystemColor.Orange:
      case SystemColor.Purple:
      case SystemColor.Red:
      case SystemColor.White:
        return `var(--${color})`;
      default:
        return color;
    }
  }, [color]);

  return (
    <span
      // class icon conflicts with admin's classes
      // TODO: rename icn to icon
      className={classnames("icn", className, {
        disabled: isDisabled,
        hover_color: !!hoverColor,
        pointer: !isDisabled && !!onClick
      })}
      {...props}
      onClick={isDisabled ? undefined : onClick}
      style={style}
      {...getTooltipAttributes(tooltip)}
    >
      {props.isLoading ? (
        <Preloader isFontSize type="spinner" />
      ) : (
        <i
          title={title}
          className={classnames(icon?.match(/\bfa[dblrs]?\s*/) ? icon : `fa fa-${icon}`)}
          style={{ color: finalColor, verticalAlign: "middle" }}
        />
      )}
    </span>
  );
};

export default Icon;

export type IconWithProgressProps = Omit<IconProps, "onClick" | "isLoading"> & {
  onClick: (e: React.MouseEvent) => Promise<void>;
};
export const IconWithProgress = ({ onClick, ...props }: IconWithProgressProps) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const _onClick = React.useCallback(
    async (e: React.MouseEvent) => {
      setIsLoading(true);

      await onClick(e);

      setIsLoading(false);
    },
    [onClick]
  );

  return <Icon {...props} onClick={isLoading ? undefined : _onClick} isLoading={isLoading} />;
};

const SIZE_TO_FONT_SIZE = {
  [16]: 14,
  [20]: 16,
  [28]: 16,
  [36]: 16,
  [48]: 20
};
export type IconActionSize = 16 | 20 | 28 | 36 | 48;
export type IconActionProps = Omit<IconProps, "size"> & {
  isActive?: boolean;
  isNoBackground?: boolean;
  isRound?: boolean;
  size: IconActionSize;
  tooltip?: string;
};
export const IconAction = ({ className, isActive, isNoBackground, size, isRound, ref, ...props }: IconActionProps) => {
  const style = React.useMemo(
    (): React.CSSProperties => ({
      ...props.style,
      fontSize: SIZE_TO_FONT_SIZE[size],
      height: size,
      width: size
    }),
    [size]
  );

  return (
    <Icon
      {...props}
      ref={ref}
      className={classNames(className, "icn_action", {
        active: isActive,
        background: size >= 28 && !isNoBackground,
        round: isRound
      })}
      style={style}
    />
  );
};
